/**
 * App Theme Context
 * Provides dynamic color theme customization for the entire application
 * Works across landing pages (hr4india.com) and OKR Hub app (okrhub.hr4india.com)
 * Uses CSS custom properties for runtime theme switching
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Color palette definitions with carefully curated professional combinations
export interface ColorPalette {
  id: string;
  name: string;
  description: string;
  preview: string; // Primary color for preview swatch
  colors: {
    primary: {
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
    };
    accent: string; // Complementary accent
  };
}

export const COLOR_PALETTES: ColorPalette[] = [
  {
    id: 'coral',
    name: 'Coral',
    description: 'Warm & energetic',
    preview: '#E85D3B',
    colors: {
      primary: {
        50: '#FFF5F2',
        100: '#FFE8E2',
        200: '#FFD1C4',
        300: '#FFB39E',
        400: '#FF7B5C',
        500: '#E85D3B',
        600: '#C94829',
        700: '#A83A20',
        800: '#8A3019',
        900: '#6D2714',
      },
      accent: '#2D8B5F',
    },
  },
  {
    id: 'slate',
    name: 'Slate',
    description: 'Professional & trustworthy',
    preview: '#4A6FA5',
    colors: {
      primary: {
        50: '#F0F4F9',
        100: '#E1E9F3',
        200: '#C3D3E7',
        300: '#9BB5D5',
        400: '#7096C3',
        500: '#4A6FA5',
        600: '#3B5A87',
        700: '#2E4669',
        800: '#22344E',
        900: '#162234',
      },
      accent: '#E5A849',
    },
  },
  {
    id: 'forest',
    name: 'Forest',
    description: 'Natural & growth-oriented',
    preview: '#2E7D5A',
    colors: {
      primary: {
        50: '#ECFDF5',
        100: '#D1FAE5',
        200: '#A7F3D0',
        300: '#6EE7B7',
        400: '#34D399',
        500: '#2E7D5A',
        600: '#256649',
        700: '#1C4E38',
        800: '#143728',
        900: '#0B2018',
      },
      accent: '#D97706',
    },
  },
  {
    id: 'plum',
    name: 'Plum',
    description: 'Sophisticated & creative',
    preview: '#7C3AED',
    colors: {
      primary: {
        50: '#F5F3FF',
        100: '#EDE9FE',
        200: '#DDD6FE',
        300: '#C4B5FD',
        400: '#A78BFA',
        500: '#7C3AED',
        600: '#6D28D9',
        700: '#5B21B6',
        800: '#4C1D95',
        900: '#3B1778',
      },
      accent: '#10B981',
    },
  },
  {
    id: 'teal',
    name: 'Teal',
    description: 'Modern & balanced',
    preview: '#0D9488',
    colors: {
      primary: {
        50: '#F0FDFA',
        100: '#CCFBF1',
        200: '#99F6E4',
        300: '#5EEAD4',
        400: '#2DD4BF',
        500: '#0D9488',
        600: '#0F766E',
        700: '#115E59',
        800: '#134E4A',
        900: '#0F3D3A',
      },
      accent: '#F59E0B',
    },
  },
  {
    id: 'charcoal',
    name: 'Charcoal',
    description: 'Minimal & refined',
    preview: '#374151',
    colors: {
      primary: {
        50: '#F9FAFB',
        100: '#F3F4F6',
        200: '#E5E7EB',
        300: '#D1D5DB',
        400: '#9CA3AF',
        500: '#374151',
        600: '#1F2937',
        700: '#1A1F2B',
        800: '#111827',
        900: '#0B0F19',
      },
      accent: '#DC2626',
    },
  },
];

interface ThemeContextValue {
  currentPalette: ColorPalette;
  palettes: ColorPalette[];
  setPalette: (paletteId: string) => void;
  isThemePanelOpen: boolean;
  setThemePanelOpen: (open: boolean) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = 'hr4india-theme-palette';

// Safe localStorage access with error handling for privacy mode/incognito
const safeGetItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn('Failed to access localStorage for theme preference:', error);
    return null;
  }
};

const safeSetItem = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.warn('Failed to save theme preference to localStorage:', error);
  }
};

export const AppThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentPaletteId, setCurrentPaletteId] = useState<string>(
    () => safeGetItem(STORAGE_KEY) || 'coral'
  );
  const [isThemePanelOpen, setThemePanelOpen] = useState(false);

  const currentPalette = COLOR_PALETTES.find(p => p.id === currentPaletteId) || COLOR_PALETTES[0];

  // Apply CSS custom properties when palette changes
  const applyTheme = useCallback((palette: ColorPalette) => {
    const root = document.documentElement;

    // Set primary color scale
    Object.entries(palette.colors.primary).forEach(([shade, value]) => {
      root.style.setProperty(`--color-primary-${shade}`, value);
      // Also set RGB values for opacity support
      const rgb = hexToRgb(value);
      if (rgb) {
        root.style.setProperty(`--color-primary-${shade}-rgb`, `${rgb.r}, ${rgb.g}, ${rgb.b}`);
      }
    });

    // Set accent color
    root.style.setProperty('--color-accent', palette.colors.accent);
    const accentRgb = hexToRgb(palette.colors.accent);
    if (accentRgb) {
      root.style.setProperty('--color-accent-rgb', `${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}`);
    }
  }, []);

  useEffect(() => {
    applyTheme(currentPalette);
  }, [currentPalette, applyTheme]);

  const setPalette = useCallback((paletteId: string) => {
    setCurrentPaletteId(paletteId);
    safeSetItem(STORAGE_KEY, paletteId);
  }, []);

  return (
    <ThemeContext.Provider
      value={{
        currentPalette,
        palettes: COLOR_PALETTES,
        setPalette,
        isThemePanelOpen,
        setThemePanelOpen,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useAppTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within an AppThemeProvider');
  }
  return context;
};

// Alias for backward compatibility
export const useLandingTheme = useAppTheme;

// Utility to convert hex to RGB
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}
